import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listChangelog, type ChangelogEntry } from "../api/changelog";

const CATEGORY_STYLES: Record<ChangelogEntry["category"], string> = {
  feature: "bg-green-100 text-green-800",
  fix: "bg-blue-100 text-blue-700",
  improvement: "bg-indigo-100 text-indigo-700",
  maintenance: "bg-gray-200 text-gray-600",
  notice: "bg-amber-100 text-amber-700",
};

const CATEGORY_LABELS: Record<ChangelogEntry["category"], string> = {
  feature: "New",
  fix: "Fixed",
  improvement: "Improved",
  maintenance: "Maintenance",
  notice: "Notice",
};

function dayLabel(isoDate: string): string {
  // entry_date is YYYY-MM-DD (no time component) -- parse as local calendar
  // days, not UTC instants, so "Today"/"Yesterday" match the operator's clock.
  const [y, m, d] = isoDate.split("-").map(Number);
  const entry = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - entry.getTime()) / 86_400_000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return entry.toLocaleDateString(undefined, { weekday: "long" });
  }
  return entry.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function groupByDate(entries: ChangelogEntry[]): { date: string; label: string; items: ChangelogEntry[] }[] {
  const groups: { date: string; label: string; items: ChangelogEntry[] }[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.date === entry.entry_date) {
      last.items.push(entry);
    } else {
      groups.push({ date: entry.entry_date, label: dayLabel(entry.entry_date), items: [entry] });
    }
  }
  return groups;
}

const PER_PAGE = 50;

export function ChangelogPage() {
  const [page, setPage] = useState(1);
  const [allEntries, setAllEntries] = useState<ChangelogEntry[]>([]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["changelog", page],
    queryFn: async () => {
      const resp = await listChangelog({ page, per_page: PER_PAGE });
      setAllEntries((prev) => (page === 1 ? resp.data : [...prev, ...resp.data]));
      return resp;
    },
  });

  const groups = useMemo(() => groupByDate(allEntries), [allEntries]);
  const total = data?.total ?? 0;
  const hasMore = allEntries.length < total;

  if (isLoading && page === 1) return <p className="text-gray-500">Loading changelog…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold mb-1">Changelog</h1>
      <p className="text-sm text-gray-500 mb-6">What's new, fixed, and improved on the platform.</p>

      {groups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Nothing to show yet — check back soon.
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.date}>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                {group.label}
              </h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {group.items.map((entry) => (
                  <div key={entry.changelog_id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span
                        className={`text-xs rounded px-2 py-0.5 whitespace-nowrap shrink-0 mt-0.5 ${CATEGORY_STYLES[entry.category]}`}
                      >
                        {CATEGORY_LABELS[entry.category] ?? entry.category}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900">{entry.title}</p>
                        {entry.description && (
                          <p className="text-sm text-gray-600 mt-0.5">{entry.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-6 text-center">
          <button
            className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-sm disabled:opacity-50"
            disabled={isLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            {isLoading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
